import Button from '@atoms/Button/Button';
import Fieldset from '@atoms/Fieldset/Fieldset';
import CheckboxInput from '@atoms/Inputs/CheckboxInput';
import Datepicker from '@atoms/Inputs/Datepicker';
import FileUpload from '@atoms/Inputs/FileUpload';
import SelectInput from '@atoms/Inputs/SelectInput';
import TextAreaInput from '@atoms/Inputs/TextAreaInput';
import TextInput from '@atoms/Inputs/TextInput';
import RecaptchaDisclaimer from '@atoms/RecaptchaDisclaimer/RecaptchaDisclaimer';
import {
  BG_CREME,
  COLLEAGUE_APPLICATION_CODE,
  FLEX_WORKER_APPLICATION_CODE,
  ZZP_APPLICATION_CODE,
} from '@constants';
import Error from '@molecules/Error/Error';
import Loader from '@molecules/Loader/Loader';
import Modal from '@molecules/Modal/Modal';
import PagepartBase from '@molecules/PagepartBase/PagepartBase';
import PagepartContent from '@molecules/PagepartContent/PagepartContent';
import InfoCirleIcon from '@svg/info-circle.svg';
import PencilIcon from '@svg/pencil.svg';
import useApplicationForm from '@utils/useApplicationForm';
import { sub } from 'date-fns';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';

import styles from './ApplicationForm.module.scss';
import ApplicationVacancies from './ApplicationVacancies';

const MOTIVATION_MODAL = 'motivation-modal-id';

function ApplicationForm({
  vacancyCode,
  aggregationChildren,
  aggLocationDetails,
  applicationCode = COLLEAGUE_APPLICATION_CODE,
  applicationFormRef,
}) {
  const applicationVacanciesRef = useRef();
  const minAge = useMemo(
    () => sub(new Date(), { years: 13 }).toISOString().split('T')[0],
    []
  );
  const [showMotivation, setShowMotivation] = useState(false);
  const {
    inputs,
    handleChange,
    onSubmit,
    loading,
    error,
    stage,
    progress,
    vacancies,
    vacancyApplicationError,
  } = useApplicationForm({
    aggregationChildren,
    aggLocationDetails,
    vacancyCode,
    scrollToRef: applicationFormRef,
    applicationVacanciesRef,
  });

  return (
    <PagepartBase backgroundColor={BG_CREME}>
      <div
        ref={applicationFormRef}
        id="solliciteren"
        className={styles.container}
      >
        <div className={styles.content}>
          <span />
          <PagepartContent
            title="Solliciteer direct"
            text="Bedankt voor je interesse. Vul je gegevens in, upload je cv en laat ons weten wat je motiveert."
            extraClasses="u-mb-2 u-mb-4@from-md"
          />
          <Loader
            message="Een momentje geduld alsjeblieft, we zijn bezig met het verwerken van je sollicitatie."
            progress={progress}
            stage={stage}
            loading={loading}
          />

          <form
            className={styles.form}
            action="/"
            onSubmit={onSubmit}
            hidden={loading}
          >
            <div className={styles.fieldsetContainer}>
              <Fieldset legend="Persoonlijke gegevens" disabled={loading}>
                <TextInput
                  label="Voorletters"
                  value={inputs.In}
                  onChange={handleChange}
                  id="initials"
                  name="In"
                  maxLength={15}
                  required
                />

                <div className="u-grid u-grid--name-fields u-grid--spacing-3">
                  <TextInput
                    label="Roepnaam"
                    value={inputs.CaNm}
                    onChange={handleChange}
                    id="firstname"
                    name="CaNm"
                    maxLength={80}
                    autoComplete="given-name"
                    required
                  />
                  <TextInput
                    label="Tussenvoegsel"
                    value={inputs.Is}
                    onChange={handleChange}
                    id="prefix"
                    name="Is"
                    maxLength={15}
                  />

                  <TextInput
                    label="Achternaam"
                    value={inputs.LaNm}
                    onChange={handleChange}
                    id="lastname"
                    name="LaNm"
                    maxLength={80}
                    autoComplete="family-name"
                    required
                  />
                </div>

                <div className="u-grid u-grid--2@md u-grid--spacing-3">
                  <SelectInput
                    label="Geslacht"
                    value={inputs.ViGe}
                    onChange={handleChange}
                    id="gender"
                    name="ViGe"
                    options={[
                      { value: 'M', label: 'Man' },
                      { value: 'V', label: 'Vrouw' },
                      { value: 'O', label: 'Onbekend' },
                    ]}
                    required
                  />

                  <Datepicker
                    label="Geboortedatum"
                    value={inputs.DaBi}
                    onChange={handleChange}
                    id="date-of-birth"
                    name="DaBi"
                    max={minAge}
                    required
                  />
                </div>

                <TextInput
                  label="Mobiel privé"
                  value={inputs.MbN2}
                  onChange={handleChange}
                  id="phone"
                  name="MbN2"
                  maxLength={255}
                  autoComplete="tel"
                  required
                />

                <TextInput
                  type="email"
                  label="E-mail privé"
                  value={inputs.EmA2}
                  onChange={handleChange}
                  id="email"
                  name="EmA2"
                  maxLength={255}
                  required
                />
              </Fieldset>

              {vacancies?.length > 1 && (
                <ApplicationVacancies
                  loading={loading}
                  ref={applicationVacanciesRef}
                  vacancies={vacancies}
                  handleChange={handleChange}
                  vacancyApplicationError={vacancyApplicationError}
                />
              )}

              <Fieldset legend="Adres gegevens" disabled={loading}>
                {applicationCode !== ZZP_APPLICATION_CODE ? (
                  <>
                    <div className="u-grid u-grid--3@md u-grid--spacing-3">
                      <TextInput
                        label="Straat"
                        value={inputs.Ad}
                        onChange={handleChange}
                        id="street"
                        name="Ad"
                        maxLength={60}
                        autoComplete="street-address"
                        required
                      />

                      <TextInput
                        label="Huisnummer"
                        value={inputs.HmNr}
                        onChange={handleChange}
                        id="housenumber"
                        name="HmNr"
                        maxLength={60}
                        type="number"
                        required
                      />

                      <TextInput
                        label="Toevoeging"
                        value={inputs.HmAd}
                        onChange={handleChange}
                        id="addition"
                        name="HmAd"
                        maxLength={30}
                      />
                    </div>

                    <div className="u-grid u-grid--2@md u-grid--spacing-3">
                      <TextInput
                        label="Postcode"
                        value={inputs.ZpCd}
                        onChange={handleChange}
                        id="zipcode"
                        name="ZpCd"
                        maxLength={15}
                        autoComplete="postal-code"
                        pattern="^(?:[1-9][0-9]{3} ?(?!sa|sd|ss)[a-Z]{2}|[1-9][0-9]{3}|[0-9]{5})$"
                        required
                      />

                      <TextInput
                        label="Woonplaats"
                        value={inputs.Rs}
                        onChange={handleChange}
                        id="city"
                        name="Rs"
                        maxLength={50}
                        autoComplete="address-level2"
                        required
                      />
                    </div>
                  </>
                ) : (
                  <TextInput
                    label="Woonplaats"
                    value={inputs.Rs}
                    onChange={handleChange}
                    id="city"
                    name="Rs"
                    maxLength={50}
                    autoComplete="address-level2"
                    required
                  />
                )}
                <SelectInput
                  label="Land"
                  value={inputs.CoId}
                  onChange={handleChange}
                  id="country"
                  name="CoId"
                  options={[
                    { value: 'NL', label: 'Nederland' },
                    { value: 'B', label: 'België' },
                    { value: 'D', label: 'Duitsland' },
                  ]}
                  required
                />
              </Fieldset>

              <Fieldset legend="Upload hier jouw cv" disabled={loading}>
                <FileUpload
                  helpText="Max. grootte: 20 MB"
                  label="Selecteer een bestand"
                  onChange={handleChange}
                  value={inputs.file}
                  maxSizeInMb={20}
                  id="cv-input"
                  name="file"
                />
              </Fieldset>

              <Fieldset
                legend="Waarom wil je bij Vitalis werken?"
                disabled={loading}
              >
                <Button
                  color="secondary"
                  label="Schrijf je motivatie"
                  onClick={() => setShowMotivation(true)}
                  Icon={PencilIcon}
                  iconLeft
                  noAnimation
                  aria-expanded={showMotivation}
                  aria-controls={MOTIVATION_MODAL}
                  extraClasses={styles.motivation}
                />
                <Modal
                  id={MOTIVATION_MODAL}
                  modalModifier="copyModal"
                  show={showMotivation}
                  onClose={() => setShowMotivation(false)}
                  title="Schrijf je motivatie"
                  closeCtaLabel="Opslaan"
                >
                  <TextAreaInput
                    onChange={handleChange}
                    value={inputs.Mo}
                    label="Motivatie"
                    id="motivation"
                    name="Mo"
                    rows={6}
                  />
                </Modal>
              </Fieldset>

              {[ZZP_APPLICATION_CODE, FLEX_WORKER_APPLICATION_CODE].includes(
                applicationCode
              ) && (
                <Fieldset legend="Overige gegevens" disabled={loading}>
                  {applicationCode === ZZP_APPLICATION_CODE && (
                    <>
                      <TextInput
                        label="BIG registratienummer"
                        value={inputs.U5F8BEDE64600DC34619ECCBBBF160222}
                        onChange={handleChange}
                        id="big-registration"
                        name="U5F8BEDE64600DC34619ECCBBBF160222"
                        maxLength={30}
                        type="number"
                      />

                      <TextAreaInput
                        value={inputs.U9FA557F74D947F024E0C6BB834579EC7}
                        name="U9FA557F74D947F024E0C6BB834579EC7"
                        onChange={handleChange}
                        label="Tarief"
                        id="price"
                        rows={3}
                        required
                      />
                      <p className={styles.infoMessage}>
                        <InfoCirleIcon />
                        Na ontvangst van je sollicitatie ontvang je van ons een
                        bericht om je overige documenten via een beveiligde
                        portaal aan te leveren.
                      </p>
                    </>
                  )}

                  {applicationCode === FLEX_WORKER_APPLICATION_CODE && (
                    <TextInput
                      label="Urenwens per week"
                      value={inputs.U8E0273EE46440ED8292A7A966F37F5CC}
                      onChange={handleChange}
                      id="hours-per-week"
                      name="U8E0273EE46440ED8292A7A966F37F5CC"
                    />
                  )}
                </Fieldset>
              )}

              {/* Disabled for now, this might be added in the near future */}
              {/* {applicationCode === HOLIDAY_WORKER_APPLICATION_CODE && (
              <Fieldset legend="Speeddate" disabled={loading}>
                <SelectInput
                  id="speeddate"
                  label="Ben je op de speeddate geweest?"
                  value={inputs.UEE25D364416ABB14856253BE59291158}
                  onChange={handleChange}
                  name="UEE25D364416ABB14856253BE59291158"
                  options={[
                    { value: 'J', label: 'Ja' },
                    { value: 'N', label: 'Nee' },
                  ]}
                />
              </Fieldset>
            )} */}

              <Fieldset legend="Tot slot" disabled={loading} hideLegend>
                <SelectInput
                  label="Vacature eerste keer gezien bij"
                  value={inputs.ViSo}
                  onChange={handleChange}
                  id="information-source"
                  name="ViSo"
                  options={[
                    { value: '03', label: 'Banenbeurs' },
                    { value: '25', label: 'Brabantzorg.nl' },
                    { value: '05', label: 'Een medewerker van Vitalis' },
                    { value: '21', label: 'Facebook' },
                    { value: '23', label: 'Google for jobs' },
                    { value: '01', label: 'Ik ben medewerker bij Vitalis' },
                    { value: '26', label: 'Indeed' },
                    { value: '35', label: 'Instagram' },
                    { value: '02', label: 'Koffie-uurtje' },
                    { value: '22', label: 'Linkedin' },
                    { value: '33', label: 'Nationale vacaturebank' },
                    { value: '36', label: 'Ontdekdezorg Contactpunt' },
                    { value: '24', label: 'UWV/ Werk.nl' },
                    { value: '30', label: 'Vitaal Flex' },
                    { value: '20', label: 'Vitalis website' },
                    { value: '32', label: 'Zorgnet Limburg' },
                  ]}
                  required
                />

                <CheckboxInput
                  label="Mijn gegevens mogen 1 jaar bewaard worden."
                  id="OpEm"
                  name="OpEm"
                  value={inputs.OpEm}
                  onChange={handleChange}
                />
              </Fieldset>
            </div>

            <Error error={error} />

            <Button label="Verstuur sollicitatie" submit />

            <RecaptchaDisclaimer />
          </form>
        </div>
      </div>
    </PagepartBase>
  );
}

ApplicationForm.propTypes = {
  vacancyCode: PropTypes.string,
  aggregationChildren: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
    })
  ),
  aggLocationDetails: PropTypes.shape({
    id: PropTypes.string,
    address: PropTypes.string,
    postal: PropTypes.string,
    city: PropTypes.string,
    afasCode: PropTypes.string,
  }),
  applicationCode: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  applicationFormRef: PropTypes.any,
};

export default ApplicationForm;
